import { useEffect, useState } from 'react';
import { makeStyles } from "@material-ui/core";
import clsx from 'clsx'
import Tooltip from "@material-ui/core/Tooltip/Tooltip";
import UtilService from '../../../../../../../helper/service';
import { useSelector } from 'react-redux';
import AnalysisScoreIcon from '../../../../../../atoms/creator/analyzeScoreIcon';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderStyle: 'solid',
    borderRadius: 20,
    position: 'relative',
    borderWidth: 4,
    height: 200,
  },
  overallPanel: {
    height: '100%',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 20,
    marginLeft: 4,
    width: 50,
    '& span': {
      fontSize: 33,
      fontFamily: 'Spartan-bold'
    }
  },
  participant: {
    fontSize: 23,
    fontFamily: 'Spartan-bold'
  },
  contextLabel: {
    fontSize: 15,
    fontFamily: 'Spartan-semibold',
    textTransform: 'uppercase',
    letterSpacing: 1.8
  },
  contextPanel: {
    marginTop: 18,
    marginLeft: 10,
    left: 50
  },
  score: {
    top: 0,
    position: 'absolute',
    display: 'flex',
    right: 0,
    flexDirection: 'row-reverse'
  },
  menuBox: {
    position: 'absolute',
    right: 2,
    top: 8,
  },
  iconBtn: {
    width: 40,
    borderRadius: 10,
    marginLeft: 20,
    '&:hover': {
      background: '#017AEF14'
    },
  },
  btn: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    textTransform: 'unset',
    marginRight: 20,
    marginLeft: 20,
  },
  menuItem: {
    padding: 0
  },
  menuText: {
    color: '#697683',
    fontSize: 12,
    fontFamily: 'roboto-regular'
  },
  icon: {
    borderRadius: 3,
    width: 27,
    height: 28,
    backgroundColor: '#f5f8fa',
    backgroundImage: "url(\"/assets/icons/unchecked.svg\")",
    marginRight: 10
  },
  checkedIcon: {
    marginRight: 10,
    '&:before': {
      display: 'block',
      width: 31.5,
      height: 28,
      backgroundImage: "url(\"/assets/icons/checked.svg\")",
      content: '""',
    },
  },
  checkbox: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  label: {
    color: '#697683',
    fontFamily: 'roboto-regular',
    fontSize: 14
  },
  leftMode: {
    left: 0,
    flexDirection: 'row'
  },

  rightMode: {
    right: 0,
    flexDirection: 'row'
  },
  leftPane: {
    background: '#f0f0f0',
    position: 'absolute',
    width: 56,
    height: 192,
    left: 0,
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20
  },
  rightPane: {
    background: '#f0f0f0',
    position: 'absolute',
    width: 56,
    height: 192,
    right: 0,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20
  }
}));

function ScorePane(props: any) {

  const classes = useStyles();
  const tooltipClasses = useStylesTooltip();

  const {
    immersionData,
    participant,
    graphWidth,
    safetyOverall,
    sessionStatus,
    overall,
    marginBottom
  } = props;

  const [stimulusOverallIndex, setStimulusOverallIndex] = useState(0);
  const [stimulusColorIndex, setStimulusColorIndex] = useState('Gray');
  const { module4Settings, userPreferences } = useSelector((state: any) => state.creatorOnBoarding);
  const { module4Data } = userPreferences;
  const { session, analyze } = module4Data.value;
  const { session_type, session_status } = session;
  const stimulus = analyze.stimulus;
  const { videoPlayed, videoStopped, videoPopIn, analyseStart } = module4Settings;
  const { tags } = analyze;
  const tagParticipant = tags.find((item: any) => item.tag_id === participant && participant !== 'All')

  let overall_index = immersionData ? immersionData.overall_index : safetyOverall.safety_index
  let overall_color_indicator = immersionData ? immersionData.overall_color_indicator : safetyOverall.color_indicator
  let overall_context_indicator = immersionData ? immersionData.overall_context_indicator : safetyOverall.context_indicator

  if (session_type === 'MODERATED') {
    if(overall){
      overall_index = overall.immersion_index;
      overall_color_indicator = overall.color_indicator;
      overall_context_indicator = overall.context_indicator;
    }
  }

  useEffect(() => {
    if (immersionData && (session_status === 'COMPLETED' || (session_type === 'ASYNC'))) {
      getImmersionData();
    }
  }, [videoPlayed, videoPopIn]);

  const getImmersionData = async () => {

    if (!(immersionData.data && immersionData.data.length > 0)) {
      return;
    }

    const curTime = stimulus && session_type === 'ASYNC'
      ? Math.floor(stimulus.stimulus_video_length * videoPlayed)
      : Math.round(videoPopIn);
    
    const epoch0 = session_type === 'MODERATED' ? (analyseStart) * 1000 : UtilService.getLocalEpoch2(immersionData.data[0].time_stamp) * 1000;
    
    const immersion = immersionData.data.find((item: any) => {
      const epoch = UtilService.getLocalEpoch2(item.time_stamp) * 1000;
      const immersionTime = (epoch - epoch0) / 1000;
      return immersionTime === curTime;
    })

    if (immersion) {
      setStimulusOverallIndex(immersion.immersion_index);
      setStimulusColorIndex(UtilService.getColorFromImmersion(immersion));
    } else {
      setStimulusOverallIndex(1);
      setStimulusColorIndex('gray');
    }
  }

  const videoStoppedAvailable = !videoStopped || (videoStopped && session_type === 'MODERATED' && videoPopIn > 1)

  return (
    <div
      className={classes.root}
      style={{
        width: graphWidth + 120,
        borderColor: UtilService.refactorGraphColor(overall_index > 1 ? overall_color_indicator : 'gray'),
        marginBottom: marginBottom ? marginBottom : 65
      }}>
      <div className={classes.leftPane} /><div className={classes.rightPane} />
      <div className={clsx(classes.score, classes.leftMode)}>
        {(!videoStoppedAvailable ) ? <div
          className={classes.overallPanel}
          style={{ color: UtilService.refactorGraphColor(overall_index > 1 ? overall_color_indicator : 'gray') }}
        >
          {(session_type === 'MODERATED' && sessionStatus === 'LIVE') ? (
            <Tooltip classes={tooltipClasses} title="Second-by-Second Immersion" placement="bottom" arrow>
              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <AnalysisScoreIcon color={overall_index > 1 ? overall_color_indicator : 'gray'} type={immersionData ? 'immersion' : 'safety'} />
                <span>{overall_index > 1 ? overall_index : '--'}</span>
              </div>
            </Tooltip>
          ) : (
            <>
              <AnalysisScoreIcon color={overall_index > 1 ? overall_color_indicator : 'gray'} type={immersionData ? 'immersion' : 'safety'} />
              <span>{overall_index > 1 ? overall_index : '--'}</span>
            </>
          )}
        </div> : <div className={classes.overallPanel} />}

        <div
          className={classes.contextPanel}
          style={{ color: UtilService.refactorGraphColor(overall_index > 1 ? overall_color_indicator : 'gray') }}
        >
          <div className={classes.participant}>{participant === 'All' ? 'All Participants' : tagParticipant.tag_name}</div>
          <div className={classes.contextLabel}>{overall_context_indicator}</div>
        </div>

      </div>

      { immersionData && videoStoppedAvailable &&
        <div className={clsx(classes.score,
          (sessionStatus === 'COMPLETED'
          ) && classes.rightMode)}>
          <div
            className={classes.overallPanel}
            style={{ color: UtilService.refactorGraphColor(stimulusOverallIndex > 1 ? stimulusColorIndex : 'gray') }}
          >
            <Tooltip classes={tooltipClasses} title="Second-by-Second Immersion" placement="bottom" arrow>
              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <AnalysisScoreIcon color={stimulusOverallIndex > 1 ? stimulusColorIndex : 'gray'} type={'immersion'} />
                <span>{stimulusOverallIndex > 1 ? stimulusOverallIndex : '--'}</span>
              </div>
            </Tooltip>
          </div>
        </div>
      }

    </div>
  );
}

export default ScorePane;

const useStylesTooltip = makeStyles(() => ({
  arrow: {
    color: '#393C41'
  },
  tooltip: {
    backgroundColor: '#393C41'
  }
}));